/** @jsx jsx */

/** @jsxFrag */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { createContext, useState } from 'react';

import { PageGridContainer, PageNotification, PageNotificationList } from '@coursera/cds-core';
import type { PageNotificationProps, Theme } from '@coursera/cds-core';

export type ProfileNotificationInputType = {
  title?: string;
  severity?: PageNotificationProps['severity'];
  message: string;
};

export type ProfileNotificationType = ProfileNotificationInputType & {
  id: string;
};

export type ProfileNotificationContextType = {
  sendNotification: (notification: ProfileNotificationInputType) => void;
};

type Props = {
  children: React.ReactElement;
};

const NUM_OF_DISPLAY_ITEMS = 3;

export const ProfileNotificationContext = createContext<ProfileNotificationContextType>({
  sendNotification: () => undefined,
});

const styles = {
  pageGridContainer: (theme: Theme) => css`
    ${theme.breakpoints.down('xs')} {
      padding: ${theme.spacing(0)};
    }
  `,
  notificationList: (theme: Theme) => css`
    width: 100%;

    ul > li {
      padding-top: ${theme.spacing(16)};
    }
  `,
  withPaddingX: (theme: Theme) => css`
    padding: ${theme.spacing(0, 48)};

    ${theme.breakpoints.between('xs', 'sm')} {
      padding: ${theme.spacing(0, 24)};
    }

    ${theme.breakpoints.down('xs')} {
      padding: ${theme.spacing(0)};
    }
  `,
  withPaddingTop: (theme: Theme) => css`
    padding-top: ${theme.spacing(16)};
  `,
  withPositionSticky: css`
    position: sticky;
    z-index: 10001;
    top: 65px;
  `,
};

const generateId = () => Math.random().toString(36).substring(2);

export const ProfileNotificationsProvider = ({ children }: Props) => {
  const [notifications, setNotifications] = useState<Array<ProfileNotificationType>>([]);

  const sendNotification = (notification: ProfileNotificationInputType) => {
    setNotifications((prev) => [...prev, { ...notification, id: generateId() }]);
  };

  const dismissNotification = (notification: ProfileNotificationType) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
  };

  return (
    <ProfileNotificationContext.Provider value={{ sendNotification }}>
      {notifications.length > 0 && (
        <div css={[styles.withPaddingTop, styles.withPositionSticky]}>
          <PageGridContainer css={styles.pageGridContainer}>
            <PageNotificationList css={[styles.notificationList, styles.withPaddingX]}>
              {notifications.slice(0 - NUM_OF_DISPLAY_ITEMS).map((notification) => (
                <PageNotification
                  key={notification.id}
                  title={notification.title}
                  severity={notification.severity}
                  onDismiss={() => dismissNotification(notification)}
                >
                  {notification.message}
                </PageNotification>
              ))}
            </PageNotificationList>
          </PageGridContainer>
        </div>
      )}
      {children}
    </ProfileNotificationContext.Provider>
  );
};
